import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatCents, formatPercent } from '../lib/format';

type SortKey =
  | 'name'
  | 'keyword'
  | 'source'
  | 'spend_cents'
  | 'clicks'
  | 'attributed_patients'
  | 'avg_ticket_cents'
  | 'revenue_cents'
  | 'cac_cents'
  | 'roi_pct'
  | 'clicks_tracked'
  | 'clicks_matched'
  | 'patients_matched'
  | 'coverage_pct';

interface Overview {
  spend_cents: number;
  impressions: number;
  clicks: number;
  new_patients: number;
  returning_patients: number;
  new_patients_ltv_cents: number;
  cac_proxy_cents: number;
  roi_proxy_pct: number | null;
}

interface AttribRow {
  campaign_id: number;
  name: string;
  source: string;
  keyword: string | null;
  spend_cents: number;
  clicks: number;
  attributed_patients: number;
  total_visits: number;
  revenue_cents: number;
  avg_ticket_cents: number;
  cac_cents: number;
  roi_pct: number | null;
}

interface PatientRow {
  vissmed_id: number;
  name: string | null;
  phone_e164: string | null;
  first_specialty: string | null;
  cadastro_at: string;
  last_visit: string | null;
  total_visits: number;
  total_cents: number;
  ticket_cents: number;
}

interface RealRow {
  campaign_id: number;
  name: string;
  source: string;
  spend_cents: number;
  clicks_tracked: number;
  clicks_matched: number;
  patients_matched: number;
  revenue_cents: number;
  avg_ticket_cents: number;
  cac_cents: number;
  roi_pct: number | null;
  coverage_pct: number | null;
}

function isoDaysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function Dashboard() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [, setOv] = useState<Overview | null>(null);
  const [attrib, setAttrib] = useState<AttribRow[]>([]);
  const [real, setReal] = useState<RealRow[]>([]);
  const [mode, setMode] = useState<'estimado' | 'real'>('estimado');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<{ camp: AttribRow; rows: PatientRow[]; loading: boolean } | null>(null);
  const [search, setSearch] = useState('');
  const [kwFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('spend_cents');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  }

  async function openDrilldown(camp: AttribRow) {
    setDrilldown({ camp, rows: [], loading: true });
    const { data, error: e } = await supabase.rpc('campaign_attributed_patients', {
      p_campaign_id: camp.campaign_id,
      p_from: from,
      p_to: to,
    });
    if (e) {
      setErr(e.message);
      setDrilldown(null);
      return;
    }
    setDrilldown({ camp, rows: (data ?? []) as PatientRow[], loading: false });
  }

  useEffect(() => {
    setLoading(true);
    (async () => {
      const [
        { data: ovData, error: ovErr },
        { data: aData, error: aErr },
        { data: rData, error: rErr },
      ] = await Promise.all([
        supabase.rpc('tracking_overview_range', { p_from: from, p_to: to }),
        supabase.rpc('campaign_attribution_range', { p_from: from, p_to: to, p_source: null }),
        supabase.rpc('campaign_attribution_real', { p_from: from, p_to: to, p_source: null }),
      ]);
      const e = ovErr?.message || aErr?.message || rErr?.message;
      if (e) setErr(e);
      else setErr(null);
      setOv((ovData && (ovData as Overview[])[0]) || null);
      setAttrib((aData ?? []) as AttribRow[]);
      setReal((rData ?? []) as RealRow[]);
      setLoading(false);
    })();
  }, [from, to]);

  function applyFilterSort<T extends Record<string, unknown>>(rows: T[]): T[] {
    let out = rows;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => String(r.name ?? '').toLowerCase().includes(q));
    }
    if (kwFilter) out = out.filter((r) => r.keyword === kwFilter);
    if (sourceFilter) out = out.filter((r) => r.source === sourceFilter);
    out = [...out].sort((a, b) => {
      const av = a[sortKey as keyof T];
      const bv = b[sortKey as keyof T];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      return sortDir === 'desc'
        ? String(bv).localeCompare(String(av))
        : String(av).localeCompare(String(bv));
    });
    return out;
  }

  const attribFiltered = useMemo(() => applyFilterSort(attrib as unknown as Record<string, unknown>[]) as unknown as AttribRow[], [attrib, search, kwFilter, sourceFilter, sortKey, sortDir]);
  const realFiltered = useMemo(() => applyFilterSort(real as unknown as Record<string, unknown>[]) as unknown as RealRow[], [real, search, kwFilter, sourceFilter, sortKey, sortDir]);

  const realTotals = real.reduce(
    (acc, r) => ({
      spend: acc.spend + (r.spend_cents ?? 0),
      tracked: acc.tracked + (r.clicks_tracked ?? 0),
      matched: acc.matched + (r.clicks_matched ?? 0),
      patients: acc.patients + (r.patients_matched ?? 0),
    }),
    { spend: 0, tracked: 0, matched: 0, patients: 0 },
  );

  function totalsBySource(src: 'meta' | 'google') {
    const spendClicks = attrib
      .filter((r) => (r.source ?? '').toLowerCase() === src)
      .reduce(
        (acc, r) => ({
          spend: acc.spend + (r.spend_cents ?? 0),
          clicks: acc.clicks + (r.clicks ?? 0),
        }),
        { spend: 0, clicks: 0 },
      );
    const realAgg = real
      .filter((r) => (r.source ?? '').toLowerCase() === src)
      .reduce(
        (acc, r) => ({
          patients: acc.patients + (r.patients_matched ?? 0),
          revenue: acc.revenue + (r.revenue_cents ?? 0),
        }),
        { patients: 0, revenue: 0 },
      );
    return { ...spendClicks, ...realAgg };
  }
  const metaT = useMemo(() => totalsBySource('meta'), [attrib, real]);
  const googleT = useMemo(() => totalsBySource('google'), [attrib, real]);
  function cac(spend: number, pts: number) { return pts > 0 ? spend / pts : 0; }
  function roi(spend: number, rev: number) { return spend > 0 ? ((rev - spend) / spend) * 100 : null; }


  function preset(days: number) {
    setFrom(isoDaysAgo(days));
    setTo(isoToday());
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-semibold">Dashboard de Tracking</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => preset(0)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">Hoje</button>
          <button onClick={() => preset(1)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">1d</button>
          <button onClick={() => preset(7)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">7d</button>
          <button onClick={() => preset(30)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">30d</button>
          <button onClick={() => preset(90)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">90d</button>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          <span className="text-slate-400 text-sm">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm mb-4">{err}</div>}

      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span> META (Facebook/Instagram)
          </span>
          <div className="flex-1 h-px bg-blue-100"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Tile label="Investido" value={formatCents(metaT.spend)} accent="meta" />
          <Tile label="Cliques" value={metaT.clicks.toLocaleString('pt-BR')} accent="meta" />
          <Tile label="Consultas" value={metaT.patients > 0 ? metaT.patients.toLocaleString('pt-BR') : '—'} accent="meta" pending={metaT.patients === 0} />
          <Tile label="CAC" value={metaT.patients > 0 ? formatCents(cac(metaT.spend, metaT.patients)) : '—'} accent="meta" pending={metaT.patients === 0} />
          <Tile label="Receita" value={metaT.revenue > 0 ? formatCents(metaT.revenue) : '—'} accent="meta" pending={metaT.revenue === 0} />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span> GOOGLE ADS
          </span>
          <div className="flex-1 h-px bg-amber-100"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Tile label="Investido" value={formatCents(googleT.spend)} accent="google" />
          <Tile label="Cliques" value={googleT.clicks.toLocaleString('pt-BR')} accent="google" />
          <Tile label="Consultas" value={googleT.patients > 0 ? googleT.patients.toLocaleString('pt-BR') : '—'} accent="google" pending={googleT.patients === 0} />
          <Tile label="CAC" value={googleT.patients > 0 ? formatCents(cac(googleT.spend, googleT.patients)) : '—'} accent="google" pending={googleT.patients === 0} />
          <Tile label="Receita" value={googleT.revenue > 0 ? formatCents(googleT.revenue) : '—'} accent="google" pending={googleT.revenue === 0} />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-600"></span> CONSOLIDADO (Meta + Google)
          </span>
          <div className="flex-1 h-px bg-slate-200"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Tile label="Investido total" value={formatCents(metaT.spend + googleT.spend)} />
          <Tile label="Cliques totais" value={(metaT.clicks + googleT.clicks).toLocaleString('pt-BR')} />
          <Tile label="Consultas totais" value={metaT.patients + googleT.patients > 0 ? (metaT.patients + googleT.patients).toLocaleString('pt-BR') : '—'} pending={metaT.patients + googleT.patients === 0} />
          <Tile label="CAC médio" value={metaT.patients + googleT.patients > 0 ? formatCents(cac(metaT.spend + googleT.spend, metaT.patients + googleT.patients)) : '—'} pending={metaT.patients + googleT.patients === 0} />
          <Tile label="ROI" value={metaT.revenue + googleT.revenue > 0 ? formatPercent(roi(metaT.spend + googleT.spend, metaT.revenue + googleT.revenue)) : '—'} pending={metaT.revenue + googleT.revenue === 0} />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 flex items-start gap-2 mt-2">
          <span>⏳</span>
          <div>
            <b>Consultas / CAC / Receita zerados</b> — aguardando tracking real. Pixel GTM capturou <b>75 cliques</b>, mas nenhum virou match com paciente Vissmed ainda. Falta rodar o matcher WHU (gclid do Google + #shortcode do Meta) contra os telefones cadastrados. Assim que casar, os números aparecem aqui.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Resumo do período</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Kv k="Período" v={`${from} → ${to}`} />
            <Kv k="CPC médio (Meta + Google)" v={metaT.clicks + googleT.clicks > 0 ? formatCents((metaT.spend + googleT.spend) / (metaT.clicks + googleT.clicks)) : '—'} />
            <Kv k="Cliques trackeados (pixel)" v={realTotals.tracked.toLocaleString('pt-BR')} />
            <Kv k="Consultas ⏳" v={realTotals.patients > 0 ? realTotals.patients.toLocaleString('pt-BR') : '—'} />
            <Kv k="CAC ⏳" v={realTotals.patients > 0 ? formatCents(cac(metaT.spend + googleT.spend, realTotals.patients)) : '—'} />
            <Kv k="Receita ⏳" v={metaT.revenue + googleT.revenue > 0 ? formatCents(metaT.revenue + googleT.revenue) : '—'} />
            <Kv k="ROI ⏳" v={realTotals.patients > 0 ? formatPercent(roi(metaT.spend + googleT.spend, metaT.revenue + googleT.revenue)) : '—'} tone={realTotals.patients > 0 ? 'good' : undefined} />
          </div>
          <p className="text-xs text-slate-400 mt-4 leading-5">
            Dados de spend e cliques vêm direto das APIs Meta/Google Ads. Consultas/CAC/receita só aparecem depois que o matcher WHU casa gclid/#shortcode com telefone Vissmed.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Como usar</h2>
          <p className="text-xs text-slate-500 leading-5">
            Use os botões <b>🔵 Meta</b> / <b>🟠 Google</b> na tabela pra isolar uma plataforma de cada vez e otimizar. Clique nos headers pra ordenar por gasto, cliques, etc. Enquanto o tracking real não tiver match, foque em spend × cliques e CPC.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-8">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-slate-700">
            Campanhas — {from} → {to}
          </h2>
          <div className="flex gap-2 text-xs">
            <button
              onClick={() => setMode('estimado')}
              className={`px-3 py-1.5 rounded-md border ${mode === 'estimado' ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              Lista (gasto)
            </button>
            <button
              onClick={() => setMode('real')}
              className={`px-3 py-1.5 rounded-md border ${mode === 'real' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              📊 Real (gclid / #sid)
            </button>
          </div>
        </div>
        <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2 text-xs">
          <span className="text-slate-500 font-medium">Filtrar fonte:</span>
          <button
            onClick={() => setSourceFilter('')}
            className={`px-2.5 py-1 rounded-md border ${sourceFilter === '' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-300 hover:bg-slate-100'}`}
          >Todas</button>
          <button
            onClick={() => setSourceFilter('meta')}
            className={`px-2.5 py-1 rounded-md border ${sourceFilter === 'meta' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300 hover:bg-blue-50'}`}
          >🔵 Meta</button>
          <button
            onClick={() => setSourceFilter('google')}
            className={`px-2.5 py-1 rounded-md border ${sourceFilter === 'google' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-slate-300 hover:bg-amber-50'}`}
          >🟠 Google</button>
          <input
            type="text"
            placeholder="Buscar campanha…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ml-auto px-2.5 py-1 border border-slate-300 rounded-md w-56"
          />
        </div>
        {mode === 'real' && (
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-200 text-xs text-emerald-800 flex gap-6">
            <span>Cliques trackeados: <b>{realTotals.tracked.toLocaleString('pt-BR')}</b></span>
            <span>Cliques com telefone: <b>{realTotals.matched.toLocaleString('pt-BR')}</b></span>
            <span>Pacientes atribuídos: <b>{realTotals.patients.toLocaleString('pt-BR')}</b></span>
            <span>Cobertura: <b>{realTotals.tracked > 0 ? ((realTotals.matched / realTotals.tracked) * 100).toFixed(1) + '%' : '—'}</b></span>
          </div>
        )}
        <div className="overflow-x-auto">
          {mode === 'estimado' ? (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase select-none">
                <tr>
                  <Th label="Campanha" k="name" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Fonte" k="source" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Keyword" k="keyword" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Gasto" k="spend_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Cliques" k="clicks" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Consultas ⏳" k="attributed_patients" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Ticket médio ⏳" k="avg_ticket_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="Receita ⏳" k="revenue_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="CAC ⏳" k="cac_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <Th label="ROI ⏳" k="roi_pct" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && <tr><td colSpan={10} className="px-5 py-6 text-center text-slate-400">Carregando…</td></tr>}
                {!loading && attribFiltered.length === 0 && <tr><td colSpan={10} className="px-5 py-6 text-center text-slate-400">Sem gasto no período.</td></tr>}
                {attribFiltered.map((r) => {
                  const src = (r.source ?? '').toLowerCase();
                  const badge = src === 'meta'
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : src === 'google'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-slate-50 text-slate-500 border-slate-200';
                  const realRow = real.find((x) => x.campaign_id === r.campaign_id);
                  const patients = realRow?.patients_matched ?? 0;
                  const revenue = realRow?.revenue_cents ?? 0;
                  const cacVal = realRow?.cac_cents ?? 0;
                  const roiVal = patients > 0 ? (realRow?.roi_pct ?? null) : null;
                  const avgTicket = patients > 0 ? revenue / patients : 0;
                  const dim = 'text-slate-300';
                  return (
                    <tr key={r.campaign_id} className="hover:bg-indigo-50 cursor-pointer" onClick={() => openDrilldown(r)}>
                      <td className="px-4 py-2 font-medium text-indigo-700 underline decoration-dotted">{r.name}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase border rounded-full px-2 py-0.5 ${badge}`}>
                          {src === 'meta' ? '🔵 Meta' : src === 'google' ? '🟠 Google' : (r.source ?? '—')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{r.keyword ?? '—'}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{formatCents(r.spend_cents)}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{r.clicks.toLocaleString('pt-BR')}</td>
                      <td className={`px-3 py-2 text-right ${patients > 0 ? 'text-slate-700' : dim}`}>{patients > 0 ? patients.toLocaleString('pt-BR') : '—'}</td>
                      <td className={`px-3 py-2 text-right ${avgTicket > 0 ? 'text-slate-700' : dim}`}>{avgTicket > 0 ? formatCents(avgTicket) : '—'}</td>
                      <td className={`px-3 py-2 text-right ${revenue > 0 ? 'text-slate-700' : dim}`}>{revenue > 0 ? formatCents(revenue) : '—'}</td>
                      <td className={`px-3 py-2 text-right ${cacVal > 0 ? 'text-slate-700' : dim}`}>{cacVal > 0 ? formatCents(cacVal) : '—'}</td>
                      <td className={`px-3 py-2 text-right font-medium ${
                        roiVal === null ? dim : roiVal > 0 ? 'text-emerald-600' : 'text-rose-600'
                      }`}>{roiVal !== null ? formatPercent(roiVal) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-emerald-50 text-emerald-700 text-xs uppercase select-none">
                <tr>
                  <Th label="Campanha" k="name" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Fonte" k="source" align="left" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Gasto" k="spend_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Cliques trackeados" k="clicks_tracked" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Com telefone" k="clicks_matched" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Pacientes (match)" k="patients_matched" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Receita" k="revenue_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="CAC" k="cac_cents" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="ROI" k="roi_pct" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                  <Th label="Cobertura" k="coverage_pct" align="right" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} tone="emerald" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && <tr><td colSpan={10} className="px-5 py-6 text-center text-slate-400">Carregando…</td></tr>}
                {!loading && realFiltered.length === 0 && <tr><td colSpan={10} className="px-5 py-6 text-center text-slate-400">Sem gasto no período.</td></tr>}
                {!loading && realTotals.tracked === 0 && realFiltered.length > 0 && (
                  <tr>
                    <td colSpan={10} className="px-5 py-3 text-center text-amber-700 bg-amber-50 text-xs">
                      Tracking ainda não começou a capturar cliques. GTM + job WHU precisam estar ativos.
                    </td>
                  </tr>
                )}
                {realFiltered.map((r) => (
                  <tr key={r.campaign_id} className="hover:bg-emerald-50">
                    <td className="px-4 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs uppercase">{r.source}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{formatCents(r.spend_cents)}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{r.clicks_tracked.toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{r.clicks_matched.toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{r.patients_matched.toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{formatCents(r.revenue_cents)}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{r.cac_cents > 0 ? formatCents(r.cac_cents) : '—'}</td>
                    <td className={`px-3 py-2 text-right font-medium ${
                      r.roi_pct !== null && r.roi_pct > 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}>{formatPercent(r.roi_pct)}</td>
                    <td className="px-3 py-2 text-right text-slate-500 text-xs">{r.coverage_pct !== null ? r.coverage_pct + '%' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {drilldown && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDrilldown(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Pacientes atribuídos — {from} → {to}</div>
                <h3 className="text-lg font-semibold text-slate-800 mt-0.5">{drilldown.camp.name}</h3>
                <div className="text-xs text-slate-500 mt-1">
                  keyword <b>{drilldown.camp.keyword ?? '—'}</b> · gasto {formatCents(drilldown.camp.spend_cents)} · {drilldown.camp.attributed_patients} pacientes · receita {formatCents(drilldown.camp.revenue_cents)}
                </div>
              </div>
              <button onClick={() => setDrilldown(null)} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Nome</th>
                    <th className="px-4 py-2 text-left">Telefone</th>
                    <th className="px-4 py-2 text-left">Especialidade</th>
                    <th className="px-4 py-2 text-left">Cadastro</th>
                    <th className="px-4 py-2 text-left">Última consulta</th>
                    <th className="px-4 py-2 text-right">Serviços Vissmed</th>
                    <th className="px-4 py-2 text-right">Receita total do paciente</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {drilldown.loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>}
                  {!drilldown.loading && drilldown.rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nenhum paciente bateu com a keyword.</td></tr>}
                  {drilldown.rows.map((p) => (
                    <tr key={p.vissmed_id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-800">{p.name ?? '—'}</td>
                      <td className="px-4 py-2 text-slate-600 text-xs font-mono">{p.phone_e164 ?? '—'}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs">{p.first_specialty ?? '—'}</td>
                      <td className="px-4 py-2 text-slate-600 text-xs">{p.cadastro_at ? p.cadastro_at.slice(0, 10) : '—'}</td>
                      <td className="px-4 py-2 text-slate-600 text-xs">{p.last_visit ?? '—'}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{p.total_visits}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{formatCents(p.total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, tone, accent, pending }: { label: string; value: string | number; tone?: 'good' | 'warn' | 'danger'; accent?: 'meta' | 'google'; pending?: boolean }) {
  const border = accent === 'meta' ? 'border-blue-200 bg-blue-50/30' : accent === 'google' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 bg-white';
  const valueColor = pending ? 'text-slate-300'
    : tone === 'good' ? 'text-emerald-600'
    : tone === 'warn' ? 'text-amber-600'
    : tone === 'danger' ? 'text-red-600'
    : 'text-slate-900';
  return (
    <div className={`border rounded-lg p-4 ${border} relative`}>
      <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        {label}
        {pending && <span className="text-[9px] text-slate-400" title="Aguardando tracking real">⏳</span>}
      </div>
      <div className={`text-xl font-semibold mt-1 ${valueColor}`}>{value}</div>
    </div>
  );
}

function Th({ label, k, align, sortKey, sortDir, onClick, tone }: {
  label: string;
  k: SortKey;
  align: 'left' | 'right';
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
  tone?: 'emerald';
}) {
  const active = sortKey === k;
  const icon = !active ? '⇅' : sortDir === 'desc' ? '↓' : '↑';
  const hover = tone === 'emerald' ? 'hover:bg-emerald-100' : 'hover:bg-slate-100';
  return (
    <th
      onClick={() => onClick(k)}
      className={`px-3 py-2 ${align === 'left' ? 'text-left' : 'text-right'} cursor-pointer ${hover} transition-colors`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-800 font-bold' : 'text-slate-300'}`}>{icon}</span>
      </span>
    </th>
  );
}

function Kv({ k, v, tone }: { k: string; v: string | number; tone?: 'good' | 'warn' }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{k}</div>
      <div className={`font-semibold ${tone === 'good' ? 'text-emerald-600' : tone === 'warn' ? 'text-amber-600' : 'text-slate-800'}`}>{v}</div>
    </div>
  );
}
